import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { observer } from 'mobx-react-lite'
import { Alert, Button, Icon, Switch, Tooltip, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { LINK_HELP_SMART_CALL_FLOW } from 'shared/constants/links'
import { logger } from 'shared/lib'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { SmartCallFlowBuilder } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder'
import { SmartCallEditStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/edit/store/SmartCallEditStore'
import styles from './styles.module.scss'

export const SmartCallFlow = observer(() => {
  const entities = useEntitiesContext()
  const { id } = useParams()
  const [store] = useState(() => new SmartCallEditStore(entities, Number(id)))

  const { init, inbox, hasInboxWorkflowEnabled, onActivateInboxWorkflow, hasEnabled } = store

  useEffect(() => void init(), [])

  const shouldDisableSmartCallFlow = !inbox?.number?.voice
  const { pathname } = useLocation()

  const [loading, setLoading] = useState(false)
  const handleInboxWorkflowStatus = async (status: boolean) => {
    try {
      setLoading(true)
      await onActivateInboxWorkflow(status)
    } catch (e) {
      logger.error(e)
    } finally {
      setLoading(false)
    }
  }
  const handleEdit = () => {
    uiStore.changeRoute({ path: pathname + '/edit' })
  }
  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <div className={styles.left}>
          <div className={styles.row}>
            <Typography ariaLabel={'title'} variant={'body-lg-medium'}>
              Smart call flow
            </Typography>
            <Tooltip
              label={
                <>
                  Set up call routing by adding steps <br /> like menus, forwarding, and voicemail.
                  <br />
                  <a href={`${LINK_HELP_SMART_CALL_FLOW}`} target='_blank' rel='noreferrer'>
                    Learn more
                  </a>
                </>
              }
              placement={'top'}
            >
              <Icon
                icon={'questionFull'}
                color={'var(--content-neutral-primary)'}
                fontSize={12}
                padding={2}
              />
            </Tooltip>
          </div>
          <Typography ariaLabel={'desc'} variant={'body-md-regular'} color={'tertiary'}>
            Simplify inbound call handling with flexible flows
          </Typography>
        </div>
        <div className={styles.right}>
          <Switch
            size={'middle'}
            value={hasInboxWorkflowEnabled}
            onChange={handleInboxWorkflowStatus}
            disabled={shouldDisableSmartCallFlow || loading}
          />
        </div>
      </div>
      {shouldDisableSmartCallFlow && (
        <Alert
          item={{
            type: 'warning',
            desc: 'Inbound calls are disabled for this inbox. Enable them to access call flow.',
          }}
          className={styles.alert}
        />
      )}
      <div className={styles.flow}>
        <SmartCallFlowBuilder
          store={store.smartCallFlowBuilderStore}
          viewOnly
          fitViewOnChange
          disabled={!hasEnabled}
        />
        {!shouldDisableSmartCallFlow && (
          <div className={styles.edit}>
            <Button text={'Edit flow'} size={'small'} contained={'primary'} onClick={handleEdit} />
          </div>
        )}
      </div>
    </div>
  )
})
